import { evaluateP2PActivationGate } from './activationGate';
import { buildP2PSourcePayload } from './sourceBoundary';
import type { P2PAppealEvaluationInput, P2PEvaluationResult, P2PEvidenceCard } from './types';

function buildEvidenceCards(input: P2PAppealEvaluationInput): P2PEvidenceCard[] {
  const source = buildP2PSourcePayload(input);

  if (source.status !== 'available') {
    return [];
  }

  return source.payload.evidenceFacts.map(fact => ({
    id: fact.id,
    title: fact.label,
    source: fact.source,
    detail: fact.value,
  }));
}

function sourceLabel(input: P2PAppealEvaluationInput): string {
  return input.submittedArtifact?.type === 'post_op_report'
    ? 'submitted post-op report'
    : 'submitted pre-auth packet';
}

export function evaluateP2PAppeal(input: P2PAppealEvaluationInput): P2PEvaluationResult {
  const activation = evaluateP2PActivationGate(input);

  if (activation.status === 'inactive') {
    return {
      status: 'inactive',
      reason: activation.reason,
    };
  }

  const source = buildP2PSourcePayload(input);

  if (source.status === 'unavailable') {
    return {
      status: 'unavailable',
      reason: 'source_unavailable',
      missingFacts: source.missing,
    };
  }

  const payload = source.payload;
  const evidenceCards = buildEvidenceCards(input);
  const missingFacts = evidenceCards.length === 0 ? ['submitted_packet_evidence'] : [];
  const artifactLabel = sourceLabel(input);
  const deadline = payload.payerResponse.deadline ?? null;

  return {
    status: 'ready',
    caseId: payload.caseId,
    primaryAction: 'review_call_script',
    deadline,
    evidenceCards,
    tacticalScripts: [
      `For this peer-to-peer review, I am discussing the ${artifactLabel} and payer denial dated ${payload.payerResponse.responseDate}.`,
      'Please state your name, role, and orthopedic specialty for the case record.',
      `The submitted record states: ${payload.submittedArtifact.summaryText}`,
      'I am requesting the payer determination connected to this peer-to-peer review be documented with a reference number.',
    ],
    opsEscalation: {
      pathway: input.requestKind === 'post_service' ? 'post_service_peer_to_peer' : 'pre_service_peer_to_peer',
      summary: `${payload.payer.name} denied the submitted request and opened peer-to-peer review.`,
      deadline,
    },
    missingFacts,
    auditSummary: `P2P evaluated from ${artifactLabel}, payer denial response, and persisted patient/case identity only.`,
  };
}
